'use client';
/*
 * Documentation:
 * Text Field — https://app.subframe.com/6fc7f5fbb098/library?component=Text+Field_be48ca43-f8e7-4c0e-8870-d219ea11abfe
 */

import * as React from 'react';
import * as SubframeCore from '@subframe/core';
import * as SubframeUtils from '../utils';

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'placeholder'> {
  placeholder?: React.ReactNode;
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  { placeholder, className, ...otherProps }: InputProps,
  ref,
) {
  return (
    <input
      className={SubframeUtils.twClassNames(
        'text-body font-body text-default-font h-full w-full border-none bg-transparent outline-none placeholder:text-neutral-400',
        className,
      )}
      placeholder={placeholder as string}
      ref={ref}
      {...otherProps}
    />
  );
});

interface TextFieldRootProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  disabled?: boolean;
  error?: boolean;
  variant?: 'outline' | 'filled';
  label?: React.ReactNode;
  helpText?: React.ReactNode;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  placeholder?: React.ReactNode;
  type?: string;
}

const TextFieldRoot = React.forwardRef<HTMLLabelElement, TextFieldRootProps>(function TextFieldRoot(
  {
    disabled = false,
    error = false,
    variant = 'outline',
    label,
    helpText,
    icon = null,
    iconRight = null,
    children,
    className,
    placeholder,
    type,
    ...otherProps
  }: TextFieldRootProps,
  ref,
) {
  return (
    <label
      className={SubframeUtils.twClassNames(
        'group/be48ca43 flex flex-col items-start gap-1.5 rounded-[8px]',
        className,
      )}
      ref={ref}
      {...otherProps}
    >
      {label ? (
        <span className="text-caption-bold font-caption-bold text-default-font">{label}</span>
      ) : null}
      <div
        className={SubframeUtils.twClassNames(
          'bg-default-background flex h-10 w-full flex-none items-center gap-1 rounded-[8px] border border-solid border-neutral-300 px-2 transition-all duration-200',
          {
            'bg-default-background border-neutral-200 bg-neutral-100 hover:border-neutral-300':
              variant === 'filled',
            'border-error-600': error,
            'cursor-not-allowed border-neutral-200 bg-neutral-50': disabled,
            'focus-within:border-brand-600 focus-within:ring-brand-600/10 focus-within:ring-4':
              !error && !disabled,
            'focus-within:border-error-600 focus-within:ring-error-600/10 focus-within:ring-4':
              error && !disabled,
          },
        )}
      >
        {icon ? (
          <SubframeCore.IconWrapper className="text-body font-body text-subtext-color">
            {icon}
          </SubframeCore.IconWrapper>
        ) : null}
        {children ? (
          <div className="flex shrink-0 grow basis-0 flex-col items-start self-stretch px-1">
            {children}
          </div>
        ) : null}
        {iconRight ? (
          <SubframeCore.IconWrapper
            className={SubframeUtils.twClassNames('text-body font-body text-subtext-color', {
              'text-error-500': error,
            })}
          >
            {iconRight}
          </SubframeCore.IconWrapper>
        ) : null}
      </div>
      {helpText ? (
        <span
          className={SubframeUtils.twClassNames('text-caption font-caption text-subtext-color', {
            'text-error-700': error,
          })}
        >
          {helpText}
        </span>
      ) : null}
    </label>
  );
});

export const TextField = Object.assign(TextFieldRoot, {
  Input,
});

export type { TextFieldRootProps };
